/**
 * Every word on the settings screen, its sections and its knobs.
 *
 * Chrome, in the register [src/text/AGENTS.md](./AGENTS.md) calls chrome: the
 * game is paused or has not started, so this is plain sentences rather than voice.
 *
 * A knob's label and hint live here rather than beside its `apply`, for the reason every other
 * word does: the screen, the pause and any later list of settings all render the same string, and
 * wording that lives at one of those use sites is wording that drifts from the other two.
 *
 * A hint is one short line or nothing. The label and the control say what a knob is; a hint says
 * the one thing they cannot, a unit or a side effect, and a knob with nothing of the kind to say
 * leaves it empty rather than restating its own label.
 */

import type { BindableActionId } from '@/core/input/bindings/bindingContract';
import type { QualityTier } from '@/render/post/qualityTier';
import type { SettingSection } from '@/ui/settings/settingContract';

export const SETTINGS_TITLE = 'Settings';
/** On the button that opens it, from the menu. */
export const SETTINGS_OPEN = 'Settings';
/** On the button that closes it, from either way in. */
export const SETTINGS_CLOSE = 'Close';
/** The title over the save panel, which this screen mounts rather than rebuilds. */
export const SETTINGS_SAVE_SECTION = 'Save file';

/** One title per section, in the knob's own vocabulary rather than the screen's. */
export const SETTING_SECTION_TITLES: Readonly<Record<SettingSection, string>> = {
  controls: 'Controls',
  audio: 'Audio',
  video: 'Video',
  gameplay: 'Gameplay',
  accessibility: 'Accessibility',
};

/**
 * Degrees of view per pointer count, named as the quantity it is. A player who wants the same feel
 * on two machines can read the number out; a multiplier would only mean something next to a
 * default nobody can see.
 */
export const SETTING_LOOK_LABEL = 'Look sensitivity';
export const SETTING_LOOK_HINT = 'Degrees turned per count of mouse or thumb movement.';

/**
 * A multiplier rather than a second sensitivity, because it is one: the base figure above is what
 * it multiplies, and 1.00 reads as "unchanged" without a default to compare against.
 */
export const SETTING_ADS_LOOK_LABEL = 'Aimed look multiplier';
export const SETTING_ADS_LOOK_HINT = 'Share of the look sensitivity kept while the sights are up.';

export const SETTING_INVERT_LOOK_LABEL = 'Invert vertical look';
export const SETTING_INVERT_LOOK_HINT = 'Push forward to look down.';

/**
 * The video knobs.
 *
 * The tier row offers the machine's own guess as a choice rather than as an absence, because
 * "whatever it picked" is a real answer a player can want back after trying the others.
 */
export const SETTING_QUALITY_TIER_LABEL = 'Quality tier';
export const SETTING_QUALITY_TIER_HINT = 'Auto lets the game judge this machine.';
export const SETTING_QUALITY_TIER_AUTO = 'Auto';
export const SETTING_QUALITY_TIER_NAMES: Readonly<Record<QualityTier, string>> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

export const SETTING_RENDER_SCALE_LABEL = 'Resolution scale';
export const SETTING_RENDER_SCALE_HINT = 'Below 1 trades sharpness for frame rate.';

export const SETTING_FOV_FIRST_LABEL = 'Field of view, degrees';
export const SETTING_FOV_FIRST_HINT = '';

export const SETTING_FOV_THIRD_LABEL = 'Third person field of view, degrees';
export const SETTING_FOV_THIRD_HINT = '';

/**
 * The two HUD knobs, in the gameplay section.
 *
 * Both are named for what they change rather than for who needs them. "Conventional HUD" as an
 * accessibility toggle would file a skin under a label some players will not look under, and the
 * skin is worth offering to anyone who prefers to read a number. Neither carries a hint: the two
 * option words say what the style is, and a size is a size.
 */
export const SETTING_HUD_SKIN_LABEL = 'HUD style';
export const SETTING_HUD_SKIN_HINT = '';
/** The art direction's own: health as what being hurt looks like, ammunition where the gun is. */
export const SETTING_HUD_SKIN_DIEGETIC = 'In the world';
/** High contrast bars, counts and words, in boxes. What most shooters do. */
export const SETTING_HUD_SKIN_PLAIN = 'Bars and numbers';

export const SETTING_HUD_SCALE_LABEL = 'HUD size';
export const SETTING_HUD_SCALE_HINT = '';

/**
 * The key binding rows.
 *
 * Action labels are named for the verb, on the terms the hold-mode rows state: a player rebinding
 * recognises "Sprint" and would have to go looking for "Shift". The conflict wording names the
 * holder and the two buttons beside it say the rest, because a key quietly meaning two things is
 * the bug the whole panel exists to prevent.
 */
export const SETTING_KEY_BINDINGS_LABEL = 'Key bindings';
export const SETTING_KEY_BINDINGS_HINT = 'Press a row, then a key. Esc backs out.';
export const SETTING_BINDING_PRESS = 'Press a key';
export const SETTING_BINDING_RESERVED = 'Esc and the function keys are reserved.';
export function bindingConflictNote(holderLabel: string): string {
  return `Already on ${holderLabel}.`;
}
export const SETTING_BINDING_TAKE = 'Take it';
export const SETTING_BINDING_CANCEL = 'Cancel';
export const SETTING_BINDING_RESET = 'Reset';
export const SETTING_BINDING_RESET_ALL = 'Reset all bindings';

export const SETTING_BINDING_ACTION_LABELS: Readonly<Record<BindableActionId, string>> = {
  moveForward: 'Move forward',
  moveBack: 'Move back',
  moveLeft: 'Move left',
  moveRight: 'Move right',
  sprint: 'Sprint',
  crouch: 'Crouch',
  jump: 'Jump',
  fire: 'Fire',
  aim: 'Aim down sights',
  reload: 'Reload',
  inspect: 'Inspect weapon',
  interact: 'Interact, revive',
  firstAid: 'Use first aid',
  drill: 'Start the drill',
  cycleView: 'First or third person',
  swapShoulder: 'Swap shoulder',
  weaponSlot1: 'Weapon slot 1',
  weaponSlot2: 'Weapon slot 2',
  weaponSlot3: 'Weapon slot 3',
  weaponSlot4: 'Weapon slot 4',
  weaponSlot5: 'Weapon slot 5',
  weaponSlot6: 'Weapon slot 6',
};

/**
 * Hold or toggle, for the three verbs that can be either.
 *
 * Named for the verb rather than for the key, because a player rebinding later still recognises
 * "Sprint" and would have to go looking for "Shift". The two option words are the whole vocabulary
 * of the choice and are shared by all three rows, so a player reads the pair once and no row
 * explains them again. Sprint alone carries a hint, for the one way its toggle differs.
 */
export const SETTING_HOLD_MODE_HOLD = 'Hold';
export const SETTING_HOLD_MODE_TOGGLE = 'Toggle';

export const SETTING_AIM_HOLD_LABEL = 'Aiming';
export const SETTING_AIM_HOLD_HINT = '';

export const SETTING_CROUCH_HOLD_LABEL = 'Crouching';
export const SETTING_CROUCH_HOLD_HINT = '';

export const SETTING_SPRINT_HOLD_LABEL = 'Sprinting';
export const SETTING_SPRINT_HOLD_HINT = 'Toggled, it ends when you stop moving forwards.';

/** On the button beside every level, which plays a short tone through that bus. */
export const SETTING_AUDITION = 'Test';

/**
 * The four levels, in the audio section.
 *
 * Named for what each one carries rather than for a bus, because "effects bus" is a mixing desk
 * word and the player is being asked how loud gunfire is. Master is named as the thing above the
 * other three so it is obvious why moving it moves them.
 *
 * Music currently carries nothing, and its hint says so rather than pretending otherwise. A slider
 * that silently does nothing is worse than one that says what it is waiting on.
 */
export const SETTING_MASTER_LEVEL_LABEL = 'Overall volume';
export const SETTING_MASTER_LEVEL_HINT = '';

export const SETTING_EFFECTS_LEVEL_LABEL = 'Effects volume';
export const SETTING_EFFECTS_LEVEL_HINT = 'Gunfire, impacts, footsteps, voices.';

export const SETTING_MUSIC_LEVEL_LABEL = 'Music volume';
export const SETTING_MUSIC_LEVEL_HINT = 'Nothing plays through this yet.';

export const SETTING_UI_LEVEL_LABEL = 'Interface volume';
export const SETTING_UI_LEVEL_HINT = '';

/** On the switch itself, beside it, so the state is a word and not only a position. */
export const SETTING_TOGGLE_ON = 'On';
export const SETTING_TOGGLE_OFF = 'Off';

/**
 * The accessibility section.
 *
 * Named for what each one does to the game rather than for the condition it answers to. "Reduce
 * motion" is a thing the game will do; "motion sickness" is a diagnosis nobody should have to file
 * themselves under to find a switch, and plenty of players want a steadier picture for reasons
 * that are nobody's business.
 *
 * The master says it turns the rest off, and each of the rest is named for what it is. A player
 * who does not yet know which of them is making them ill throws the master; a player who does
 * turns that one back on.
 */
export const SETTING_MOTION_REDUCTION_LABEL = 'Reduce motion';
export const SETTING_MOTION_REDUCTION_HINT = 'Turns off everything below.';

export const SETTING_HEAD_BOB_LABEL = 'Head bob';
export const SETTING_HEAD_BOB_HINT = '';

export const SETTING_CAMERA_SHAKE_LABEL = 'Camera shake';
export const SETTING_CAMERA_SHAKE_HINT = 'Off changes nothing about where rounds go.';

/**
 * The two channels that are not motion.
 *
 * Rarity colour is worded as what is left rather than as what is lost, because the marks and the
 * word are the answer either way and the colour is the part that some players were never getting.
 */
export const SETTING_RARITY_COLOUR_LABEL = 'Rarity colour';
export const SETTING_RARITY_COLOUR_HINT = 'Off leaves the marks and the word.';

export const SETTING_AUDIO_CAPTIONS_LABEL = 'Sound captions';
export const SETTING_AUDIO_CAPTIONS_HINT = '';
